<script setup lang="ts">
/**
 * UiKitTabs — табы HeroUI UI-kit. Figma: 1991-15316.
 * Цвета только из токенов (классы hui-*). Контент панелей — именованные слоты по key.
 */
import { BG, BORDER, TEXT, TEXT_FG, type HuiColor, type HuiSize } from './palette'

interface TabItem { key: string | number, label: string, icon?: string, disabled?: boolean }
type TabsVariant = 'solid' | 'bordered' | 'light' | 'underlined'
type TabsRadius = 'none' | 'sm' | 'md' | 'lg' | 'full'

const props = withDefaults(
  defineProps<{
    items: TabItem[]
    color?: HuiColor
    variant?: TabsVariant
    size?: HuiSize
    radius?: TabsRadius
    fullWidth?: boolean
    /** Рендерить область панели под табами (false — использовать как селектор) */
    panel?: boolean
  }>(),
  { color: 'primary', variant: 'solid', size: 'md', radius: 'md', fullWidth: false, panel: true },
)

const model = defineModel<string | number>()

defineOptions({ name: 'UiKitTabs' })

onMounted(() => {
  if (model.value === undefined && props.items.length) model.value = props.items[0].key
})

function select(item: TabItem) {
  if (item.disabled) return
  model.value = item.key
}

const RADIUS: Record<TabsRadius, string> = {
  none: 'rounded-none', sm: 'rounded-hui-sm', md: 'rounded-hui-md', lg: 'rounded-hui-lg', full: 'rounded-full',
}
const SIZE: Record<HuiSize, string> = { sm: 'h-7 px-3 text-xs', md: 'h-9 px-3 text-sm', lg: 'h-11 px-4 text-base' }

const listClass = computed(() => {
  const base = props.fullWidth ? 'flex w-full' : 'inline-flex'
  switch (props.variant) {
    case 'solid': { return `${base} bg-hui-default-100 p-1 gap-1 ${RADIUS[props.radius]}`
    }
    case 'bordered': { return `${base} p-1 gap-1 border-2 border-hui-default-200 ${RADIUS[props.radius]}`
    }
    case 'light': { return `${base} p-1 gap-1`
    }
    case 'underlined': { return `${base} gap-1 border-b border-hui-divider`
    }
    default: { return base
    }
  }
})

function tabClass(item: TabItem) {
  const active = model.value === item.key
  const c = props.color
  const parts = [
    'relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-hui-focus disabled:opacity-50 disabled:pointer-events-none cursor-pointer',
    SIZE[props.size],
    props.fullWidth ? 'flex-1' : '',
  ]
  const muted = 'text-hui-foreground/60 hover:text-hui-foreground'
  if (props.variant === 'underlined') {
    parts.push('rounded-none border-b-2 -mb-px', active ? `${TEXT[c]} ${BORDER[c]}` : `${muted} border-transparent`)
    return parts
  }
  // solid / bordered / light: «курсор» — фоновая плашка под активным
  if (!active) {
    parts.push(`${muted} bg-transparent ${RADIUS[props.radius]}`)
    return parts
  }
  if (props.variant === 'solid') {
    parts.push(c === 'default' ? `bg-hui-content1 shadow-sm text-hui-foreground ${RADIUS[props.radius]}` : `${BG[c]} ${TEXT_FG[c]} shadow-sm ${RADIUS[props.radius]}`)
  } else if (props.variant === 'bordered') {
    parts.push(`bg-hui-content1 shadow-sm ${TEXT[c]} ${RADIUS[props.radius]}`)
  } else {
    parts.push(`bg-hui-default-100 ${TEXT[c]} ${RADIUS[props.radius]}`)
  }
  return parts
}

const activeItem = computed(() => props.items.find(i => i.key === model.value) ?? null)
</script>

<template>
  <div>
    <div role="tablist" :class="listClass">
      <button
        v-for="item in items"
        :key="item.key"
        type="button"
        role="tab"
        :aria-selected="model === item.key"
        :disabled="item.disabled"
        :class="tabClass(item)"
        @click="select(item)"
      >
        <Icon v-if="item.icon" :name="item.icon" class="w-4 h-4 shrink-0" />
        {{ item.label }}
      </button>
    </div>

    <div v-if="panel && activeItem" class="mt-3" role="tabpanel">
      <slot :name="String(activeItem.key)" :item="activeItem">
        <slot :item="activeItem" />
      </slot>
    </div>
  </div>
</template>
